import { useAtom } from "jotai";
import { BasicInput } from "../../../../../components/BasicInput";
import { Button } from "../../../../../components/ui/button";
import { currentStep } from "../../../../../../app/store";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { BasicSelect } from "../../../../../components/BasicSelect";
import { states } from "../../../../../../app/utils/states";
import { orgs } from "@/app/utils/orgs";
import { phoneMask } from "@/app/utils/phoneFormater";
import { differenceInYears, format, isBefore, isValid, parse } from "date-fns";
import { useC6Bank } from "@/app/contexts/C6BankContext";
import masks from "@/app/utils/masks";
import { useQuery } from "@tanstack/react-query";
import { customerService } from "@/app/services/customerService";
import { useEffect } from "react";

interface PersonalInfoFormProps {
  visible?: boolean;
}

export const personalFormSchema = z
  .object({
    name: z
      .string()
      .min(1, "* obrigatório")
      .max(35, "O nome do cliente deve ter no máximo 35 caracteres"),
    birthDate: z
      .string()
      .min(1, "* obrigatório")
      .transform((value) => {
        return parse(value, "dd/MM/yyyy", new Date()).toISOString();
      })
      .refine((value) => {
        return isValid(new Date(value));
      }, "Data Inválida")
      .refine((f) => {
        const diff = differenceInYears(new Date(), new Date(f));
        return diff >= 18 && diff <= 69;
      }, "A idade deve estar entre 18 e 69 anos")
      .transform((f) => format(f, "dd/MM/yyyy")),
    cpf: z.string().optional(),
    phone: z
      .string()
      .refine((f) => f.replace(/\D/g, "").length === 11, "* Telefone inválido"),
    email: z.string().min(1, "* obrigatório").email("email invalido"),
    rg: z.object({
      number: z.string().min(1, "* obrigatório"),
      date: z
        .string()
        .min(1, "* obrigatório")
        .transform((value) => parse(value, "dd/MM/yyyy", new Date()))
        .refine((f) => {
          return isBefore(new Date(f), new Date());
        }, "data invalida")
        .transform((f) => format(f, "dd/MM/yyyy")),
      uf: z
        .string({ required_error: "* obrigatório" })
        .min(2, "* obrigatório")
        .max(2),
      org: z
        .string({ required_error: "* obrigatório" })
        .min(1, "* obrigatório"),
    }),
  })
  .refine(
    (data) => {
      const birth = parse(data.birthDate, "dd/MM/yyyy", new Date());
      const rgDate = parse(data.rg.date, "dd/MM/yyyy", new Date());

      if (!isValid(birth) || !isValid(rgDate)) return true;

      return rgDate >= birth;
    },
    {
      message: "Data de emissão não pode ser anterior à data de nascimento",
      path: ["rg", "date"],
    },
  );

type personalformData = z.infer<typeof personalFormSchema>;

export default function PersonalInfoForm({ visible }: PersonalInfoFormProps) {
  const [, setStep] = useAtom(currentStep);

  const { customerData, setCustomerData } = useC6Bank();

  const { data } = useQuery({
    queryKey: ["customer-cpf", customerData?.cpf],
    queryFn: () => customerService.customerCpf(customerData?.cpf!),
    enabled: !!customerData?.cpf,
  });

  const personalForm = useForm<personalformData>({
    resolver: zodResolver(personalFormSchema),
  });

  useEffect(() => {
    if (data) {
      personalForm.reset({
        cpf: masks.cpf(customerData?.cpf ?? ""),
        name: data.name ?? "",
        email: data.email ?? "",
        birthDate: masks.date(data.birthDate ?? ""),
        phone: masks.phone(data.phone ?? customerData?.phone ?? ""),
        rg: {
          number: data.rg?.number ?? "",
          date: masks.date(data.rg?.date ?? ""),
          org: data.rg?.org ?? "",
          uf: data.rg?.uf ?? "",
        },
      });
    }
  }, [data]);

  const {
    handleSubmit,
    formState: { errors },
  } = personalForm;

  const submitForm = (formData: personalformData) => {
    const formatDateISO = (date: string) => {
      const parsed = parse(date, "dd/MM/yyyy", new Date());

      if (!isValid(parsed)) {
        throw new Error("Data inválida");
      }

      return format(parsed, "yyyy-MM-dd");
    };

    setCustomerData({
      name: formData.name,
      email: formData.email,
      phone: formData.phone.replace(/\D/g, ""),
      cpf: customerData?.cpf,
      birthDate: formatDateISO(formData.birthDate),
      rg: {
        ...formData.rg,
        date: formatDateISO(formData.rg.date),
      },
      address: data?.address,
    });

    setStep((state) => state + 1);
  };

  return (
    <div className={visible ? "flex flex-col gap-8 max-sm:gap-4" : "hidden"}>
      <FormProvider {...personalForm}>
        <div className="grid grid-cols-inputs gap-4 max-md:grid-cols-1 max-sm:grid-cols-1 max-sm:gap-3">
          <BasicInput
            name="name"
            label="Nome completo"
            error={errors.name?.message}
          />
          <BasicInput
            name="cpf"
            label="CPF"
            disabled
            error={errors.cpf?.message}
          />
          <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1 max-sm:gap-3">
            <BasicInput
              type="date"
              name="birthDate"
              label="Data de nascimento"
              error={errors.birthDate?.message}
            />
            <BasicInput
              name="phone"
              onInput={phoneMask}
              label="Telefone"
              error={errors.phone?.message}
            />
          </div>
          <BasicInput
            name="email"
            label="E-mail"
            error={errors.email?.message}
          />
          <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1 max-sm:gap-3">
            <BasicInput
              name="rg.number"
              label="RG"
              error={errors.rg?.number?.message}
            />
            <BasicInput
              type="date"
              name="rg.date"
              label="Data de emissão"
              error={errors.rg?.date?.message}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1 max-sm:gap-3">
            <BasicSelect
              name="rg.org"
              label="Orgão emissor"
              error={errors.rg?.org?.message}
              options={orgs}
              placeholder="Selecione"
            />

            <BasicSelect
              label="Uf de emissão"
              name="rg.uf"
              options={states}
              placeholder="Selecione o seu estado"
              error={errors.rg?.uf?.message}
            />
          </div>
        </div>
      </FormProvider>
      <div className="flex justify-end max-sm:justify-center">
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white max-sm:w-full"
          onClick={handleSubmit(submitForm)}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
